import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, Sparkles, Check } from 'lucide-react';

/**
 * PaywallModal — the calm "Explore Premium" prompt that appears when a user
 * intentionally taps a Premium-only piece of content (Prompt #8 §29).
 *
 * Rules:
 *   * "Not Now" must always work — never trap the user.
 *   * Never triggered by mood/anxiety/loneliness (§31, §71).
 *   * No exaggerated urgency copy (§28).
 *   * Copy is warm, direct, non-manipulative.
 */
const PaywallModal = ({ open, onClose, contentTitle }) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/55 backdrop-blur-md animate-fade-in"
      onClick={onClose}
      data-testid="paywall-overlay"
    >
      <div
        className="w-full max-w-[440px] bg-[#FFFCF3] rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden animate-slide-up"
        onClick={(e) => e.stopPropagation()}
        data-testid="paywall-modal"
      >
        <div className="px-6 pt-6 pb-2 flex items-start justify-between">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #C79A4A 0%, #6b4c1a 100%)', color: '#0D2B24' }}>
              <Sparkles className="w-5 h-5" />
            </span>
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: '#C79A4A' }}>Reflections Premium</p>
          </div>
          <button
            onClick={onClose}
            data-testid="paywall-close"
            aria-label="Close"
            className="w-9 h-9 rounded-full bg-[#E8DDCB] hover:bg-[#DBC9A3] flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4 text-stone-700" />
          </button>
        </div>
        <div className="px-6 pt-2 pb-6">
          <h2 className="text-2xl leading-snug text-stone-800" style={{ fontFamily: "'DM Serif Display', serif" }}>
            {contentTitle
              ? <>&ldquo;{contentTitle}&rdquo; is part of Premium.</>
              : 'Go deeper with Reflections.'}
          </h2>
          <p className="text-[13px] text-stone-500 mt-2 leading-relaxed">
            More space to breathe, reflect, heal, rest, and grow — whenever you need it.
          </p>
          <ul className="mt-4 space-y-2">
            {['Full meditation library', 'Full Reflections by Rowdy audio', 'Premium sleep experiences', 'Full soundscape library', 'Premium programs & videos'].map((f) => (
              <li key={f} className="flex items-center gap-2 text-[13px] text-stone-700">
                <Check className="w-4 h-4" style={{ color: '#C79A4A' }} />{f}
              </li>
            ))}
          </ul>
          <div className="mt-6 flex gap-2">
            <button
              onClick={() => { onClose?.(); navigate('/membership'); }}
              data-testid="paywall-explore-cta"
              className="flex-1 px-5 py-3 rounded-xl text-sm font-semibold active:scale-95 transition-transform"
              style={{ background: '#173F35', color: '#FFFCF6' }}
            >
              Explore Premium
            </button>
            <button
              onClick={onClose}
              data-testid="paywall-not-now"
              className="px-5 py-3 rounded-xl text-sm font-semibold bg-transparent text-stone-500 hover:text-stone-700"
            >
              Not now
            </button>
          </div>
          <p className="text-[10px] text-stone-400 text-center mt-3">
            Your journal, community, mood check-ins, breathing, and daily reflection stay free — always.
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PaywallModal;
